// src/pages/Comunidad.js
import React, { useMemo, useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import {
    FaBullhorn,
    FaCalendarAlt,
    FaHandsHelping,
    FaRocketchat,
    FaFileAlt,
    FaUserEdit,
    FaCheckCircle,
    FaLightbulb,
    FaEnvelope,
    FaArrowRight,
    FaQuestionCircle,
    FaLock
} from 'react-icons/fa';
import Swal from 'sweetalert2';
import { db, functions } from '../firebase';
import { collection, query, onSnapshot, where, getDocs, getDoc, Timestamp, orderBy, doc, updateDoc, arrayUnion } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { useAuth } from '../context/AuthContext';
import { useModulos } from '../hooks/useModulos';
import { useRestriccionMorosidad } from '../hooks/useRestriccionMorosidad';
import MandatoryNotificationModal from '../components/MandatoryNotificationModal';
import './Comunidad.css';
import './ComunidadMobile.css';

const Comunidad = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { userData, currentUser, updateLocalUserData } = useAuth();
    const { modulos } = useModulos(userData?.fraccionamientoId);
    const { isTabRestricted, isSubSectionRestricted } = useRestriccionMorosidad();
    const fraccionamientoId = userData?.fraccionamientoId;
    const role = String(userData?.rol || '').toLowerCase();
    const isResident = ['user', 'residente', 'inquilino', 'propietario'].includes(role);
    const isAdmin = ['superadmin', 'admin', 'manager', 'plataforma', 'admin_fracc'].includes(role);
    const votacionesPath = isResident ? '/comunidad/votaciones-avisos' : '/comunidad/avisos';

    // Detectar si estamos en la ruta principal (solo /comunidad) o en una subruta
    const isMainRoute = location.pathname === '/comunidad';

    // Detectar si es móvil (solo mostrar métricas en móvil cuando es ruta principal)
    const [isMobile, setIsMobile] = useState(window.innerWidth <= 768);

    useEffect(() => {
        const handleResize = () => {
            setIsMobile(window.innerWidth <= 768);
        };
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Estados para métricas
    const [metrics, setMetrics] = useState({
        votacionesActivas: 0,
        votacionesPendientes: 0,
        propuestasNuevas: 0,
        eventosProximos: 0,
        mensajesSinLeer: 0,
        solicitudesEnProceso: 0,
        contactosRegistrados: 0
    });
    const [loading, setLoading] = useState(true);

    // Estados para Notificación Obligatoria
    const [mandatoryNotice, setMandatoryNotice] = useState({
        show: false,
        id: null,
        title: '',
        description: '',
        extraInfo: ''
    });

    const monthNames = [
        "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
        "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre"
    ];

    // Obtener métricas
    useEffect(() => {
        if (!fraccionamientoId) {
            setLoading(false);
            return;
        }

        const unsubscribers = [];

        // Votaciones activas
        const votacionesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'votaciones');
        const votacionesQuery = query(votacionesRef, orderBy('fechaCreacion', 'desc'));
        const unsubVotaciones = onSnapshot(votacionesQuery, (snapshot) => {
            const activas = snapshot.docs.filter(doc => {
                const data = doc.data();
                const estado = data.estado || 'activa';
                return estado === 'activa';
            }).length;

            setMetrics(prev => ({
                ...prev,
                votacionesActivas: activas
            }));
        }, (error) => {
            console.error('Error al obtener votaciones:', error);
        });
        unsubscribers.push(unsubVotaciones);

        // Propuestas nuevas (esta semana)
        const inicioSemana = new Date();
        inicioSemana.setHours(0, 0, 0, 0);
        inicioSemana.setDate(inicioSemana.getDate() - inicioSemana.getDay());
        const inicioSemanaTimestamp = Timestamp.fromDate(inicioSemana);

        const propuestasRef = collection(db, 'fraccionamientos', fraccionamientoId, 'propuestasIncidencias');
        const propuestasQuery = query(propuestasRef, orderBy('fechaCreacion', 'desc'));
        const unsubPropuestas = onSnapshot(propuestasQuery, (snapshot) => {
            const ahora = new Date();
            const inicioSemana = new Date(ahora);
            inicioSemana.setDate(inicioSemana.getDate() - inicioSemana.getDay());
            inicioSemana.setHours(0, 0, 0, 0);

            const nuevas = snapshot.docs.filter(doc => {
                const fecha = doc.data().fechaCreacion?.toDate?.() || new Date(doc.data().fechaCreacion);
                return fecha >= inicioSemana;
            }).length;

            setMetrics(prev => ({
                ...prev,
                propuestasNuevas: nuevas
            }));
        }, (error) => {
            console.error('Error al obtener propuestas:', error);
        });
        unsubscribers.push(unsubPropuestas);

        // Eventos próximos (este mes)
        const inicioMes = new Date();
        inicioMes.setHours(0, 0, 0, 0);
        inicioMes.setDate(1);
        const finMes = new Date(inicioMes);
        finMes.setMonth(finMes.getMonth() + 1);

        const eventosRef = collection(db, 'fraccionamientos', fraccionamientoId, 'eventosCitas');
        const unsubEventos = onSnapshot(eventosRef, (snapshot) => {
            const ahora = new Date();
            const finMes = new Date(ahora.getFullYear(), ahora.getMonth() + 1, 0);

            const proximos = snapshot.docs.filter(doc => {
                const data = doc.data();
                const fecha = data.fecha?.toDate?.() || new Date(data.fecha);
                return fecha >= ahora && fecha <= finMes;
            }).length;

            setMetrics(prev => ({
                ...prev,
                eventosProximos: proximos
            }));
        }, (error) => {
            console.error('Error al obtener eventos:', error);
        });
        unsubscribers.push(unsubEventos);

        // Mensajes sin leer del chat
        if (currentUser?.uid) {
            const chatsRef = collection(db, 'fraccionamientos', fraccionamientoId, 'chats');
            const unsubChats = onSnapshot(chatsRef, async (snapshot) => {
                let totalUnread = 0;
                for (const chatDoc of snapshot.docs) {
                    const chatData = chatDoc.data();
                    const unreadCount = chatData.unreadCounts?.[currentUser.uid] || 0;
                    totalUnread += unreadCount;
                }
                setMetrics(prev => ({
                    ...prev,
                    mensajesSinLeer: totalUnread
                }));
            }, (error) => {
                console.error('Error al obtener chats:', error);
            });
            unsubscribers.push(unsubChats);
        }

        // Solicitudes en proceso (Métricas en tiempo real)
        const solicitudesRef = collection(db, 'fraccionamientos', fraccionamientoId, 'documentos');
        let solicitudesQuery;
        if (isAdmin) {
            solicitudesQuery = query(
                solicitudesRef,
                where('segmento', '==', 'solicitudes')
            );
        } else if (currentUser?.uid) {
            solicitudesQuery = query(
                solicitudesRef,
                where('segmento', '==', 'solicitudes'),
                where('uploadedBy', '==', currentUser.uid)
            );
        }

        if (solicitudesQuery) {
            const unsubSolicitudes = onSnapshot(solicitudesQuery, (snapshot) => {
                const enProceso = snapshot.docs.filter(d => {
                    const data = d.data();
                    const est = String(data.estado || 'pendiente').toLowerCase();
                    return est === 'pendiente' || est === 'revision' || est === 'en_proceso';
                }).length;

                setMetrics(prev => ({
                    ...prev,
                    solicitudesEnProceso: enProceso
                }));
            }, (error) => {
                console.error('Error al obtener solicitudes para métricas:', error);
            });
            unsubscribers.push(unsubSolicitudes);
        }

        // Proveedores y contactos registrados (Métricas en tiempo real)
        const proveedoresRef = collection(db, 'fraccionamientos', fraccionamientoId, 'proveedores');
        const unsubProveedores = onSnapshot(proveedoresRef, (snapshot) => {
            setMetrics(prev => ({
                ...prev,
                contactosRegistrados: snapshot.docs.length
            }));
        }, (error) => {
            console.error('Error al obtener proveedores para métricas:', error);
        });
        unsubscribers.push(unsubProveedores);

        setLoading(false);

        return () => {
            unsubscribers.forEach(unsub => {
                if (typeof unsub === 'function') {
                    unsub();
                }
            });
        };
    }, [fraccionamientoId, isAdmin, currentUser?.uid]);

    // Helpers para caché de cuotas vistas por usuario y evitar bucle en navegación
    const getCuotasVistasLocales = () => {
        try {
            const key = `cuotas_vistas_${currentUser?.uid || 'anon'}_${fraccionamientoId || ''}`;
            const item = localStorage.getItem(key);
            return item ? JSON.parse(item) : [];
        } catch {
            return [];
        }
    };

    const registrarCuotaVistaLocal = (feeId) => {
        try {
            const key = `cuotas_vistas_${currentUser?.uid || 'anon'}_${fraccionamientoId || ''}`;
            const actuales = getCuotasVistasLocales();
            const setIds = new Set(actuales.map(String));
            setIds.add(String(feeId));
            localStorage.setItem(key, JSON.stringify(Array.from(setIds)));
        } catch (e) {
            console.warn('Error al guardar cuota en caché local:', e);
        }
    };

    // Verificar Cuotas Extraordinarias para Notificación Obligatoria
    useEffect(() => {
        if (!isResident || !fraccionamientoId || !currentUser?.uid) return;

        const checkExtraFees = async () => {
            try {
                const fraccDoc = await getDoc(doc(db, 'fraccionamientos', fraccionamientoId));
                if (!fraccDoc.exists()) return;

                const configFinanciera = fraccDoc.data()?.configuracionFinanciera || {};
                const cuotaExtras = Array.isArray(configFinanciera.cuotaExtras) ? configFinanciera.cuotaExtras : [];
                
                if (cuotaExtras.length === 0) return;

                // Obtener IDs ya vistos en Firestore y en caché local
                const vistasFirestore = Array.isArray(userData?.vistasNotificaciones)
                    ? userData.vistasNotificaciones.map(String)
                    : [];
                const vistasLocales = getCuotasVistasLocales().map(String);
                const vistasSet = new Set([...vistasFirestore, ...vistasLocales]);

                // Encontrar la primera cuota que no haya sido vista
                const newFee = cuotaExtras.find(fee => fee.id && !vistasSet.has(String(fee.id)));

                if (newFee) {
                    setMandatoryNotice({
                        show: true,
                        id: String(newFee.id),
                        title: 'Nueva Cuota Extraordinaria',
                        description: `Se ha configurado una nueva cuota por concepto de: "${newFee.descripcion}" con un monto de $${newFee.monto}.`,
                        extraInfo: `Esta cuota se empezará a aplicar a partir del mes de ${monthNames[(newFee.mesInicio || 1) - 1]}. Por favor, toma tus previsiones.`
                    });
                }
            } catch (error) {
                console.error('Error al verificar cuotas extraordinarias:', error);
            }
        };

        checkExtraFees();
    }, [isResident, fraccionamientoId, currentUser?.uid, userData?.vistasNotificaciones]);

    const handleCloseMandatoryNotice = async () => {
        if (!mandatoryNotice.id || !currentUser?.uid) return;
        const feeId = String(mandatoryNotice.id);

        // Guardar inmediatamente en caché local para prevenir bucles al navegar
        registrarCuotaVistaLocal(feeId);

        // Actualizar el estado en memoria de AuthContext si está disponible
        if (updateLocalUserData) {
            const vistasActuales = Array.isArray(userData?.vistasNotificaciones)
                ? userData.vistasNotificaciones.map(String)
                : [];
            if (!vistasActuales.includes(feeId)) {
                updateLocalUserData({
                    vistasNotificaciones: [...vistasActuales, feeId]
                });
            }
        }

        // Cerrar modal de inmediato en UI
        setMandatoryNotice(prev => ({ ...prev, show: false }));

        try {
            const userRef = doc(db, 'usuarios', currentUser.uid);
            await updateDoc(userRef, {
                vistasNotificaciones: arrayUnion(feeId)
            });

            // Registrar log para el Manager usando Cloud Function
            try {
                const logSystemNotification = httpsCallable(functions, 'logSystemNotification');
                await logSystemNotification({
                    fraccionamientoId,
                    titulo: 'Notificación de Cuota Leída',
                    descripcion: `El residente ${userData?.nombre || 'Desconocido'} ha confirmado enterado de la cuota: "${mandatoryNotice.title}".`,
                    actorNombre: userData?.nombre || 'Residente',
                    actorRol: userData?.rol || 'residente',
                    actorRolGrupo: 'usuario',
                    categoria: 'comunicacion',
                    tipoMovimiento: 'notificacion_leida',
                    origen: 'web'
                });
            } catch (fnError) {
                console.error('Error al enviar log a Cloud Function:', fnError);
            }
        } catch (error) {
            console.error('Error al marcar notificación como leída en Firestore:', error);
        }
    };

    // Definimos las tarjetas funcionales mejoradas
    const communityOptions = useMemo(() => {
        console.log('🔵 [Comunidad] Evaluando opciones de comunidad:', {
            modulos,
            chat_comunitario: modulos.chat_comunitario,
            comunidad: modulos.comunidad,
            tipo_chat_comunitario: typeof modulos.chat_comunitario,
            es_true: modulos.chat_comunitario === true,
            es_false: modulos.chat_comunitario === false,
            valor_exacto: modulos.chat_comunitario,
            fraccionamientoId
        });

        const options = [
            {
                subKey: 'votaciones_avisos',
                title: 'Votaciones y Avisos',
                subtitle: 'Participa en decisiones importantes',
                icon: <FaBullhorn />,
                description: 'Vota en propuestas comunitarias, consulta resultados y mantente informado sobre avisos importantes del fraccionamiento.',
                path: votacionesPath,
                status: `${metrics.votacionesActivas} votaciones activas`,
                action: 'Ver todas',
                color: '#3b82f6', // Azul
                bgColor: '#dbeafe'
            },
            {
                subKey: 'propuestas_incidencias',
                title: 'Propuestas e Incidencias',
                subtitle: 'Reporta y sugiere mejoras',
                icon: <FaLightbulb />,
                description: 'Presenta nuevas propuestas para mejorar el fraccionamiento o reporta incidencias que requieran atención.',
                path: '/comunidad/propuestas',
                status: `${metrics.propuestasNuevas} propuestas nuevas`,
                action: 'Crear propuesta',
                color: '#f59e0b', // Ámbar
                bgColor: '#fef3c7'
            },
            {
                subKey: 'eventos_citas',
                title: 'Eventos y Citas',
                subtitle: 'Calendario comunitario',
                icon: <FaCalendarAlt />,
                description: 'Consulta eventos próximos, agenda citas con la administración y mantente al día con las actividades comunitarias.',
                path: '/comunidad/eventos',
                status: `${metrics.eventosProximos} eventos próximos`,
                action: 'Ver calendario',
                color: '#14b8a6', // Verde azulado (teal)
                bgColor: '#ccfbf1'
            },
            // Mostrar Chat Comunitario solo si está habilitado
            ...(modulos.chat_comunitario === true ? (() => {
                console.log('✅ [Comunidad] Agregando Chat Comunitario a las opciones');
                return [{
                    subKey: 'chat_comunitario',
                    title: 'Chat Comunitario',
                    subtitle: 'Comunícate en tiempo real',
                    icon: <FaRocketchat />,
                    description: 'Conversa con otros residentes, comparte información y mantente conectado con la comunidad del fraccionamiento.',
                    path: '/comunidad/chat',
                    status: `${metrics.mensajesSinLeer} mensajes nuevos`,
                    action: 'Abrir chat',
                    color: '#10b981', // Verde
                    bgColor: '#d1fae5'
                }];
            })() : (() => {
                console.log('❌ [Comunidad] NO se agrega Chat Comunitario (modulos.chat_comunitario !== true)');
                return [];
            })()),
            {
                subKey: 'solicitudes_permisos',
                title: 'Solicitudes y Permisos',
                subtitle: 'Gestiona trámites',
                icon: <FaFileAlt />,
                description: 'Tramita permisos de mudanza, construcción, remodelación y consulta los requisitos necesarios para cada gestión.',
                path: '/comunidad/documentos',
                status: `${metrics.solicitudesEnProceso} ${metrics.solicitudesEnProceso === 1 ? 'solicitud en proceso' : 'solicitudes en proceso'}`,
                action: 'Nueva solicitud',
                color: '#8b5cf6', // Púrpura
                bgColor: '#ede9fe'
            },
            {
                subKey: 'directorio_contactos',
                title: 'Directorio de Contactos',
                subtitle: 'Información de proveedores',
                icon: <FaUserEdit />,
                description: 'Consulta la agenda de proveedores autorizados y la información fiscal del fraccionamiento para tus trámites.',
                path: '/comunidad/contacto-proveedores',
                status: `${metrics.contactosRegistrados} ${metrics.contactosRegistrados === 1 ? 'contacto registrado' : 'contactos registrados'}`,
                action: 'Ver directorio',
                color: '#6b7280', // Gris
                bgColor: '#f3f4f6'
            }
        ];
        console.log('✅ [Comunidad] Opciones finales generadas:', {
            totalOpciones: options.length,
            tieneChat: options.some(opt => opt.title === 'Chat Comunitario'),
            opciones: options.map(opt => opt.title)
        });
        return options;
    }, [votacionesPath, metrics, modulos.chat_comunitario, modulos, fraccionamientoId]);

    // Si estamos en la ruta principal y es móvil, mostrar solo métricas con accesos rápidos
    const showMetricsOnly = isMainRoute && isMobile && (role === 'user' || role === 'superadmin');

    return (
        <div className="comunidad-page-wrapper">
            {/* Modal de Notificación Obligatoria */}
            <MandatoryNotificationModal 
                isOpen={mandatoryNotice.show}
                onClose={handleCloseMandatoryNotice}
                title={mandatoryNotice.title}
                description={mandatoryNotice.description}
                extraInfo={mandatoryNotice.extraInfo}
            />

            {/* Header */}
            <header className="comunidad-header">
                <div className="comunidad-header-content">
                    <div className="comunidad-header-icon">
                        <FaHandsHelping style={{ color: '#ffffff' }} />
                    </div>
                    <div className="comunidad-header-text">
                        <h1 className="comunidad-title">Comunidad</h1>
                        <p className="comunidad-subtitle">Gestiona la comunicación y participación de los residentes</p>
                    </div>
                </div>
            </header>

            {/* Métricas Clave */}
            <div className="comunidad-metrics-grid">
                <Link
                    to={votacionesPath}
                    className="comunidad-metric-card metric-clickable"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#dbeafe', color: '#3b82f6' }}>
                        <FaCheckCircle />
                    </div>
                    <div className="metric-content-wrapper">
                        <p className="metric-value">{loading ? '...' : metrics.votacionesActivas}</p>
                        <p className="metric-label">Votaciones Activas</p>
                        <p className="metric-subtext">{metrics.votacionesPendientes > 0 ? `${metrics.votacionesPendientes} pendientes por tu voto` : 'Todas al día'}</p>
                    </div>
                </Link>

                <Link
                    to="/comunidad/propuestas"
                    className="comunidad-metric-card metric-clickable"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#fef3c7', color: '#f59e0b' }}>
                        <FaLightbulb />
                    </div>
                    <div className="metric-content-wrapper">
                        <p className="metric-value">{loading ? '...' : metrics.propuestasNuevas}</p>
                        <p className="metric-label">Propuestas Nuevas</p>
                        <p className="metric-subtext">Esta semana</p>
                    </div>
                </Link>

                <Link
                    to="/comunidad/eventos"
                    className="comunidad-metric-card metric-clickable"
                    style={{ textDecoration: 'none', color: 'inherit' }}
                >
                    <div className="metric-icon-wrapper" style={{ backgroundColor: '#ccfbf1', color: '#14b8a6' }}>
                        <FaCalendarAlt />
                    </div>
                    <div className="metric-content-wrapper">
                        <p className="metric-value">{loading ? '...' : metrics.eventosProximos}</p>
                        <p className="metric-label">Próximos Eventos</p>
                        <p className="metric-subtext">Este mes</p>
                    </div>
                </Link>

                {modulos.chat_comunitario === true && (
                    <Link
                        to="/comunidad/chat"
                        className="comunidad-metric-card metric-clickable"
                        style={{ textDecoration: 'none', color: 'inherit' }}
                    >
                        <div className="metric-icon-wrapper" style={{ backgroundColor: '#d1fae5', color: '#10b981' }}>
                            <FaEnvelope />
                        </div>
                        <div className="metric-content-wrapper">
                            <p className="metric-value">{loading ? '...' : metrics.mensajesSinLeer}</p>
                            <p className="metric-label">Mensajes Sin Leer</p>
                            <p className="metric-subtext">En chat comunitario</p>
                        </div>
                    </Link>
                )}
            </div>

            {/* Cards Funcionales - Solo mostrar si NO estamos en modo métricas únicamente */}
            {!showMetricsOnly && (
                <div className="comunidad-cards-grid">
                    {communityOptions.map((option, index) => {
                        const isBlocked = isResident && (isTabRestricted('comunidad') || (option.subKey && isSubSectionRestricted('comunidad', option.subKey)));
                        if (isBlocked) {
                            return (
                                <div
                                    key={index}
                                    className="comunidad-functional-card card-restringida-mora"
                                    style={{
                                        opacity: 0.72,
                                        cursor: 'pointer',
                                        position: 'relative',
                                        background: '#f8fafc',
                                        border: '1px solid #cbd5e1'
                                    }}
                                    onClick={() => {
                                        Swal.fire({
                                            title: 'Servicio Inhabilitado',
                                            text: `La sección "${option.title}" está temporalmente inhabilitada por adeudo pendiente. Puedes acudir a Finanzas para consultar y liquidar tu cuenta.`,
                                            icon: 'warning',
                                            confirmButtonText: 'Ir a Finanzas',
                                            showCancelButton: true,
                                            cancelButtonText: 'Cerrar',
                                            confirmButtonColor: '#0284c7'
                                        }).then((result) => {
                                            if (result.isConfirmed) {
                                                navigate('/finanzas');
                                            }
                                        });
                                    }}
                                >
                                    <div style={{ position: 'absolute', top: '12px', right: '14px', background: '#fee2e2', color: '#ef4444', padding: '4px 8px', borderRadius: '8px', fontSize: '0.75rem', fontWeight: 700, display: 'flex', alignItems: 'center', gap: '5px' }}>
                                        <FaLock /> Inhabilitado
                                    </div>
                                    <div className="card-header-section">
                                        <div className="card-icon-wrapper" style={{ backgroundColor: '#e2e8f0', color: '#64748b' }}>
                                            {option.icon}
                                        </div>
                                        <div className="card-title-section">
                                            <h3 className="card-title">{option.title}</h3>
                                            <p className="card-subtitle">{option.subtitle}</p>
                                        </div>
                                    </div>
                                    <p className="card-description">{option.description}</p>
                                    <div className="card-footer-section">
                                        <span className="card-status" style={{ color: '#ef4444', fontWeight: 600 }}>Bloqueado por adeudo</span>
                                        <span className="card-action" style={{ color: '#ef4444' }}>
                                            <FaLock />
                                        </span>
                                    </div>
                                </div>
                            );
                        }

                        return (
                            <Link to={option.path} key={index} className="comunidad-functional-card">
                                <div className="card-header-section">
                                    <div className="card-icon-wrapper" style={{ backgroundColor: option.bgColor, color: option.color }}>
                                        {option.icon}
                                    </div>
                                    <div className="card-title-section">
                                        <h3 className="card-title">{option.title}</h3>
                                        <p className="card-subtitle">{option.subtitle}</p>
                                    </div>
                                </div>
                                <p className="card-description">{option.description}</p>
                                <div className="card-footer-section">
                                    <span className="card-status">{option.status}</span>
                                    <span className="card-action">
                                        {option.action} <FaArrowRight />
                                    </span>
                                </div>
                            </Link>
                        );
                    })}
                </div>
            )}

            {/* Banner de Ayuda - Solo mostrar si NO estamos en modo métricas únicamente */}
            {!showMetricsOnly && (
                <div className="comunidad-help-banner">
                    <div className="help-banner-content">
                        <FaQuestionCircle className="help-banner-icon" />
                        <div className="help-banner-text">
                            <p className="help-banner-title">¿Necesitas ayuda?</p>
                            <p className="help-banner-subtitle">Contacta a la administración para cualquier consulta o asistencia</p>
                        </div>
                        <button
                            className="help-banner-button"
                            onClick={() => navigate('/configuraciones/soporte')}
                        >
                            Contactar Administración
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Comunidad;
